import { defaultBusiness as NAP } from "@/lib/site-config";
export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  content: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "when-is-my-mot-due-how-to-check",
    title: "When Is My MOT Due? How to Check (2026 Guide)",
    description: "Check when your MOT is due in seconds, get it tested early without losing days, and avoid the £1,000 fine. Woodseer Autos, E14.",
    date: "2026-05-12",
    readTime: "6 min",
    category: "MOT",
    tags: ["MOT", "East London", "E14", "DVSA"],
    content: `Your MOT certificate has an expiry date — miss it and you could be hit with a £1,000 fine, void your insurance, or have the car seized. The good news: checking is free, takes under a minute, and you can sort the test up to a month early without losing any days. Here's exactly how, written by people who run a [DVSA-authorised MOT station in East London](/mot).

## How Do I Check When My MOT Is Due?

The quickest way is the government's free MOT checker — [gov.uk/check-mot-status](https://www.gov.uk/check-mot-status). Type in your registration and it shows:

- The exact date your current MOT expires
- A full history of previous MOT tests, mileage and outcomes
- Every advisory the tester noted last time
- Whether the certificate is genuine (some sellers forge them)

You'll also find the date on your last MOT certificate, your V5C logbook reminder, your insurer's records, or — if you've booked with us before — on the email confirmation we sent after your last test. Save the gov.uk link to your phone home screen so it's a one-tap check the next time you're not sure.

## Can I Get My MOT Done Early Without Losing Days?

Yes. The DVSA lets you have your MOT done **up to one calendar month minus a day before it expires** without losing any time. Your new certificate runs from the old expiry date, not from the day of the test.

So if your MOT expires on **20 June**, you can test from **21 May** onwards and your next certificate will still run to **20 June the following year**. There's no penalty for being early — and it gives you a safety buffer to fix anything that fails before you're driving on borrowed time.

If you've left it until the last minute, [book your MOT online](/book) and we'll usually fit you in same-day or next-day.

## What Happens If I Drive Without a Valid MOT?

Driving with an expired MOT is a criminal offence in England, Scotland and Wales. The only legal exception is driving directly to a pre-booked MOT appointment.

The penalties stack up fast:

- **Up to £1,000 fine** for driving without a valid MOT
- **Insurance can be voided** — meaning if you crash, you pay for everything personally
- **Six points on your licence** if the car is also deemed dangerous
- **The vehicle can be seized** if stopped by police

Set a reminder one month before your expiry date and book the test as soon as the early-test window opens. We can also send you a free reminder by text — just ask when you're in.

## How Long Does an MOT Take and What Does It Cost?

A standard Class 4 MOT (cars and small vans) takes **45–60 minutes** in most workshops, including ours. Larger vehicles (Class 5 minibuses, Class 7 vans up to 3.5t) take a little longer.

The DVSA-set maximum fee is **£54.85** for a Class 4 MOT, but most independent garages charge less. We charge **from £35** for a Class 4 — see our [full MOT and servicing prices](/pricing) for current rates.

If your car fails, a partial retest is **free within 10 working days** if you leave it with the same garage and we do the repairs. Worth knowing.

## What If My Car Fails the MOT?

A fail isn't the end of the world — most failures are easy fixes. The most common failure reasons are:

- **Tyres** (under 1.6mm tread, sidewall damage, mismatched sizes)
- **Lights** (a single blown bulb is the #1 fail across the country)
- **Brakes** (worn pads or discs, imbalanced performance)
- **Suspension** (worn drop links, knocking bushes)
- **Wipers and washers** (torn blades, empty washer fluid)

Many of these you can spot yourself the night before — we've put together a [free 30-point pre-MOT checklist](/mot-checklist) so you can give yourself the best chance of a first-time pass.

If you fail with us, we'll quote the repairs in writing before touching anything, and the retest is free. We never carry out work without your sign-off — see our policy on the [servicing page](/servicing).

## How Often Should I Get My Car Serviced as Well as MOT'd?

This trips a lot of drivers up. **An MOT is not a service.** An MOT only checks that your car meets the legal minimum on test day — it doesn't change the oil, top up fluids, or replace worn-but-not-yet-failed parts.

Most cars need an interim service every **6 months or 6,000 miles** and a full service every **12 months or 12,000 miles** to keep the manufacturer warranty intact and the car reliable. The two most common patterns:

- **MOT and full service together once a year** — easiest to remember
- **MOT once a year, interim service in between** — better for high-mileage drivers

Both are fine. We can do them in one visit — see [servicing options](/servicing) or [book both online](/book?service=mot).

## FAQs: When Is My MOT Due

**Q: Does my MOT expiry roll over if I forget?**

No. The day after expiry, the car is illegal to drive on the road (except to a pre-booked test). It does not auto-renew under any circumstances.

**Q: Can I check someone else's MOT?**

Yes. The gov.uk checker is public — you only need the registration. Useful when buying a used car: check the MOT history before you hand over money.

**Q: My V5C reminder hasn't arrived. Is the date still valid?**

Yes. The DVSA expiry date is the legal one regardless of whether you've received a paper or text reminder. Always cross-check with the gov.uk checker.

**Q: Does the MOT test the engine?**

Not in any depth. The MOT is a roadworthiness check — lights, brakes, tyres, suspension, emissions, structure. Engine condition, gearbox health and timing-belt life are checked at a [full service](/servicing), not at MOT.

**Q: I bought the car last week and it has 3 weeks of MOT left. Do I get a fresh 12 months?**

No. The MOT runs with the car, not the owner. You inherit whatever's left. Test it early so the new certificate starts cleanly.

**Q: Are MOT prices going up in 2026?**

The DVSA cap (£54.85 for Class 4) hasn't been raised since 2010 and there's no announced change for 2026. Independent garages remain free to charge less, and most do.

**Q: Can I drive home from the MOT test if it failed?**

Only if your existing MOT is still valid that day, or you're driving to a place to get the failures repaired. Otherwise, no — even if the car feels fine.

## Book Your MOT in East London

We're a DVSA-authorised MOT testing station (V106376) on Pixley Street, E14 — testing Classes 1, 2, 3, 4, 5 and 7. Most tests are completed within the hour, with a free retest if repairs are done with us.

[Book your MOT online](/book?service=mot) · [Call ${NAP.phone}](${NAP.phoneHref}) · [See current MOT prices](/pricing)

Need help working out what your car actually needs? Read our guide on [how often to service your car](/blog/how-often-should-i-service-my-car) or the [most common MOT failure reasons and how to avoid them](/blog/mot-failure-reasons-how-to-avoid-them).`,
  },
  {
    slug: "car-warning-lights-what-they-mean",
    title: "Car Dashboard Warning Lights — What They Mean and What to Do",
    description: "A guide to the most common dashboard warning lights, what they mean, and when you need to act. From Woodseer Autos, East London.",
    date: "2026-04-07",
    readTime: "5 min",
    category: "Diagnostics",
    tags: ["Diagnostics", "Warning Lights", "Engine Management"],
    content: `A warning light on your dashboard can be anything from a gentle reminder to a serious safety alert. Knowing the difference could save you a breakdown — or a much bigger repair bill.

## Red Warning Lights — Stop and Check

Red lights are urgent. If one appears while you're driving, pull over safely as soon as you can.

- **Engine oil pressure** — Low oil level or pump failure. Driving with this on can destroy your engine. Stop immediately and check your oil level.
- **Engine temperature** — Your engine is overheating. Stop driving, let it cool down, and check your coolant level.
- **Brake system** — Could mean your handbrake is on, brake fluid is low, or there's a fault with the braking system. Check immediately.
- **Battery / charging** — Your alternator may have failed. The car will run on battery power until it dies — get to a garage quickly.

## Amber Warning Lights — Attention Needed

Amber lights mean something needs checking, but it's usually safe to drive for a short while.

- **Engine management light** — The most common one. Could be anything from a faulty sensor to a misfire. Get a diagnostic scan to find out.
- **ABS warning** — Your anti-lock braking system has a fault. Normal brakes still work, but ABS won't activate in an emergency.
- **Tyre pressure** — One or more tyres may be low on pressure. Check and inflate to the correct level.
- **DPF (Diesel Particulate Filter)** — Your DPF is getting clogged. Try a sustained motorway run, or get it checked.

## When to Get a Diagnostic Check

If a warning light stays on after you've checked the basics, bring your car in for a professional diagnostic scan. At Woodseer Autos, we use professional-grade OBD-II equipment to read fault codes and identify the actual cause — not just guess.

Call us on **${NAP.phone}** or WhatsApp us a photo of the warning light and we'll advise.`,
  },
  {
    slug: "how-often-should-i-service-my-car",
    title: "How Often Should I Service My Car? A Practical Guide",
    description: "Not sure when your car needs a service? Here's a clear guide to service intervals, what's included, and why it matters. Woodseer Autos, East London.",
    date: "2026-04-03",
    readTime: "4 min",
    category: "Servicing",
    tags: ["Servicing", "Car Maintenance", "East London"],
    content: `Regular servicing keeps your car reliable, safe and holding its value. But how often should you actually have it done?

## The General Rule

Most manufacturers recommend servicing every **12 months or 12,000 miles**, whichever comes first. Some newer vehicles have variable service intervals based on driving conditions — check your handbook or ask us.

## Interim vs Full vs Major Service

- **Interim service** — Every 6 months or 6,000 miles. Covers oil and filter change, fluid top-ups, and a basic safety check. Good for high-mileage or city driving.
- **Full service** — Every 12 months or 12,000 miles. More comprehensive: includes air filter, spark plugs (petrol), brake inspection, and a full vehicle health check.
- **Major service** — Every 24 months or 24,000 miles. Everything in a full service plus transmission fluid, coolant replacement, and additional component checks.

## Does Servicing Affect My Warranty?

No. Under UK consumer law (specifically the Block Exemption Regulation), you can have your car serviced at any independent garage without affecting your manufacturer warranty — as long as the correct parts and procedures are used.

We stamp your service book and use OE-quality or genuine parts, so your warranty stays intact.

## Signs Your Car Is Overdue a Service

- Service light is on
- Engine feels rough or sluggish
- Fuel economy has dropped
- Unusual noises or smells
- It's been more than 12 months

## Book a Service at Woodseer Autos

We offer interim, full and major servicing for all makes and models at our East London garage. Call **${NAP.phone}** to book.`,
  },
  {
    slug: "mot-failure-reasons-how-to-avoid-them",
    title: "Top MOT Failure Reasons and How to Avoid Them",
    description: "The most common reasons cars fail their MOT — and simple things you can check before your test. Expert advice from Woodseer Autos, East London.",
    date: "2026-03-28",
    readTime: "5 min",
    category: "MOT",
    tags: ["MOT", "MOT Failure", "Pre-MOT Check"],
    content: `Nearly 40% of vehicles fail their MOT on the first attempt. Many of those failures are for issues that could have been spotted and fixed beforehand. Here are the most common reasons — and what you can do about them.

## 1. Lighting and Signalling

The number one reason for MOT failures. Check every light on your car:

- Headlights (dipped and main beam)
- Brake lights
- Indicators
- Rear fog light
- Number plate light
- Reverse light

Bulbs are cheap and easy to replace. This is the easiest fail to avoid.

## 2. Tyres

Your tyres must have at least **1.6mm of tread depth** across the central three-quarters of the tyre. Check all four tyres — including the inner edges, which wear faster on some vehicles.

Also check for damage: cuts, bulges or cracks in the sidewall will cause a fail.

## 3. Brakes

Worn pads, warped discs, or seized calipers can all cause a fail. If you notice squealing, grinding, or the car pulling to one side under braking, get your brakes checked before the MOT.

## 4. Windscreen and Wipers

A chip or crack larger than 10mm in the driver's direct line of sight will fail. Outside the swept area, the limit is 40mm. Wiper blades must clear the screen effectively — replace them if they're streaking.

## 5. Suspension

Worn shock absorbers, damaged springs, and perished bushings are common failures, especially on older vehicles or cars that drive over rough roads regularly.

## 6. Emissions

Diesel vehicles in particular can fail on emissions. A well-maintained engine is less likely to have issues — regular servicing helps keep emissions in check.

## What to Do Before Your MOT

Run through a quick pre-MOT checklist:
- All lights working?
- Tyres above 1.6mm?
- Wipers clearing properly?
- Screenwash topped up?
- Horn working?
- Number plates clean and legible?

## Book Your MOT at Woodseer Autos

We're a government-authorised testing station in East London. If your car doesn't pass, we'll explain why, quote fairly for repairs, and retest for free. Call **${NAP.phone}**.`,
  },
  {
    slug: "brakes-squealing-grinding-what-it-means",
    title: "Brakes Squealing or Grinding? Here's What It Means",
    description: "Hearing a squeal or grinding noise when you brake? Here's what's causing it and when you need to act. Woodseer Autos, East London.",
    date: "2026-03-22",
    readTime: "4 min",
    category: "Repairs",
    tags: ["Brakes", "Mechanical Repairs", "Safety"],
    content: `Brake noises are one of the most common reasons people bring their car into the garage — and for good reason. Your brakes are the single most important safety system on your vehicle.

## Squealing or Squeaking

A high-pitched squeal when braking is usually caused by **worn brake pads**. Most modern pads have a metal wear indicator that contacts the disc when the pad is getting thin — that's the noise you're hearing.

**What to do:** Get your brakes inspected. If the pads are worn, they need replacing before they damage the discs.

## Grinding

A grinding sound means the pads have worn down completely and metal is contacting metal. This is **urgent** — you're damaging your brake discs, which are much more expensive to replace than pads alone.

**What to do:** Stop driving and get to a garage immediately. Continuing to drive with grinding brakes is dangerous and will significantly increase the repair cost.

## Clicking or Rattling

A clicking noise when braking can be caused by loose brake components, worn anti-rattle clips, or warped discs. It's worth investigating but usually less urgent than grinding.

## How Long Do Brake Pads Last?

Typically between 25,000 and 50,000 miles, depending on your driving style, vehicle weight, and whether you do mostly city or motorway driving. City drivers tend to wear pads faster due to frequent stopping.

## Brake Repair at Woodseer Autos

We fit quality brake pads and discs for all makes and models. Every brake job includes a full inspection of the entire braking system — pads, discs, calipers, fluid and hoses. No shortcuts.

Call us on **${NAP.phone}** if you're hearing brake noises.`,
  },
  {
    slug: "independent-garage-vs-dealer-east-london",
    title: "Independent Garage vs Dealer — Which Is Better for Your Car?",
    description: "Should you take your car to a dealer or an independent garage? An honest comparison from Woodseer Autos, your local garage in East London.",
    date: "2026-03-15",
    readTime: "5 min",
    category: "General",
    tags: ["Independent Garage", "Dealer Alternative", "East London"],
    content: `When your car needs work, you've got two main options: go back to the franchised dealer, or use an independent garage. Here's an honest comparison to help you decide.

## Cost

This is usually the biggest difference. Dealers typically charge higher labour rates and use manufacturer-branded parts, which come at a premium. An independent garage can often do the same job for significantly less, using OE-quality parts that meet the same specifications.

## Warranty

A common myth is that you must use a dealer to maintain your warranty. Under UK law (the Block Exemption Regulation), you can have your car serviced at any garage without voiding your warranty — as long as they use parts that meet the manufacturer's specifications and follow the service schedule.

We stamp your service book and use quality parts, so your warranty stays protected.

## Expertise

Dealers specialise in one brand. Independent garages work across all makes and models, which often means broader diagnostic experience. A good independent mechanic has seen thousands of different faults across dozens of manufacturers.

## Personal Service

At a dealer, you're often one of many. At an independent garage, you'll usually deal with the same people each time. We remember your car, its history, and any ongoing issues — that continuity matters.

## When a Dealer Makes Sense

- Recall work (always free at the dealer)
- Software updates specific to your manufacturer
- Specialist warranty claims that require dealer diagnostics

## When an Independent Makes Sense

- Routine servicing
- MOT testing
- General repairs and diagnostics
- Bodywork
- Anything where you want honest advice and fair pricing

## Why Woodseer Autos?

We're an independent garage in East London that works on all makes and models. We offer the same quality of work as a dealer — without the dealer price tag. And we'll always be straight with you about what your car needs.

Call **${NAP.phone}** or pop in.`,
  },
  {
    slug: "mot-test-poplar-e14-what-to-expect",
    title: "MOT Test in Poplar, E14 — What to Expect at Woodseer Autos",
    description: "Looking for an MOT test in Poplar, E14? Here's exactly what happens when you bring your car to Woodseer Autos — from booking to results.",
    date: "2026-04-12",
    readTime: "4 min",
    category: "MOT",
    tags: ["MOT", "Poplar", "E14", "East London"],
    content: `If you live or work in Poplar and your MOT is coming up, you want a testing station that's close by, reliable and straightforward. Here's what to expect when you come to Woodseer Autos.

## Where We Are

We're at 16 Pixley Street, London E14 7DF — just off Poplar High Street. If you're coming from the DLR, we're a short walk from Blackwall station. The entrance is via 14 Copenhagen Place, through the driveway past the parking spaces.

## What Happens During Your MOT

When you arrive, our technicians will take your vehicle straight in (or as soon as we're ready if it's a busy day). The test itself covers over 65 inspection points:

- Lights, indicators and reflectors
- Steering and suspension
- Brakes — condition and performance
- Tyres — tread depth, condition and pressure
- Windscreen, wipers and washers
- Exhaust emissions
- Seatbelts and seats
- Bodywork and structure
- Registration plates and VIN

Most tests take around 45 minutes to an hour.

## If Your Vehicle Fails

We'll explain exactly what failed and why — in plain English, not mechanic jargon. You'll get an honest quote for repairs, and if we do the work, the retest is free.

Most common failures we see from Poplar and E14 drivers are lighting issues, worn tyres and brake components. Many of these can be sorted same-day.

## MOT Classes We Test

We're authorised for Classes 1–5 and 7, which covers motorbikes, cars, vans, minibuses and goods vehicles up to 3,500kg. Our VTS number is V106376.

## Book Your MOT

Call us on **${NAP.phone}** or request a booking online. We're open Monday to Saturday, 9am to 6pm.`,
  },
  {
    slug: "car-servicing-near-canary-wharf",
    title: "Car Servicing Near Canary Wharf — Drop Off Before Work",
    description: "Need car servicing near Canary Wharf? Woodseer Autos is minutes away. Drop your car off before work and collect it after. All makes and models.",
    date: "2026-04-11",
    readTime: "4 min",
    category: "Servicing",
    tags: ["Servicing", "Canary Wharf", "E14", "East London"],
    content: `If you work in Canary Wharf, finding time to get your car serviced can feel impossible. But we're just a few minutes' drive from the Wharf — close enough to drop your car off in the morning and collect it after work.

## How It Works

1. **Call us to book** — we'll arrange a time that suits you
2. **Drop your car off** on your way to work
3. **We service it during the day** and call you when it's ready
4. **Collect on your way home** — or we can discuss other arrangements

## What's Included

We offer three service levels:

- **Interim Service** — oil and filter change, brake check, fluid top-ups, tyre inspection. Ideal if you're doing high mileage or it's been 6 months since your last service.
- **Full Service** — everything in an interim plus air filter, spark plugs (petrol), comprehensive brake inspection, battery check and a full vehicle health report.
- **Major Service** — our most thorough option. Includes transmission fluid, coolant replacement and additional component checks. Recommended every 24 months or 24,000 miles.

## Will It Void My Warranty?

No. Under UK consumer law, you can have your car serviced at any independent garage without affecting your manufacturer warranty. We use OE-quality parts and stamp your service book.

## Why Canary Wharf Workers Choose Us

- We're 5 minutes from the estate
- Drop-off and collect scheduling works around your hours
- All makes and models — from company cars to personal vehicles
- Honest advice, clear pricing, no hard sell
- We've serviced vehicles for DLR commuters, HSBC and Barclays staff, and local residents

## Book Your Service

Call **${NAP.phone}** or WhatsApp us. We'll find a time that works around your schedule.`,
  },
  {
    slug: "clutch-replacement-east-london-cost-guide",
    title: "Clutch Replacement in East London — Cost & Signs",
    description: "How much does a clutch replacement cost in East London? Learn the signs of a worn clutch and what's involved. Woodseer Autos, E14.",
    date: "2026-04-09",
    readTime: "5 min",
    category: "Repairs",
    tags: ["Clutch Replacement", "East London", "Mechanical Repairs"],
    content: `A worn clutch is one of the most common mechanical repairs we handle at Woodseer Autos. If you're noticing clutch problems, here's what you need to know before booking in.

## Signs Your Clutch Needs Replacing

- **Biting point has changed** — the pedal engages much higher or lower than it used to
- **Slipping** — the engine revs rise but the car doesn't accelerate proportionally, especially in higher gears or under load
- **Difficulty changing gear** — gears feel stiff or won't engage smoothly
- **Burning smell** — a distinct acrid smell when driving, particularly in traffic or on hills
- **Juddering** — the car shudders when pulling away from a standstill
- **Soft or spongy pedal** — the clutch pedal feels different underfoot

## What's Involved in a Clutch Replacement

A clutch replacement is a significant job because the gearbox needs to come out to access the clutch assembly. Here's what's typically involved:

1. Remove the gearbox (and sometimes the subframe or exhaust, depending on the vehicle)
2. Inspect the flywheel — if it's damaged or worn, it may need resurfacing or replacing
3. Fit the new clutch kit (friction plate, pressure plate, release bearing)
4. Refit the gearbox and reassemble everything
5. Road test to confirm smooth engagement and gear changes

## Flywheel Consideration

Many modern cars use a dual-mass flywheel (DMF). If your DMF is worn, replacing just the clutch means the new clutch will wear out prematurely. We'll always inspect the flywheel and advise honestly — if it's fine, we won't replace it unnecessarily.

## How Much Does It Cost?

Clutch replacement costs vary significantly depending on:
- Your vehicle make and model
- Whether the flywheel needs replacing
- Labour time (some vehicles are more complex than others)

We'll always give you a clear, itemised quote before starting any work. No surprises, no hidden charges.

## Book a Clutch Inspection

If you're experiencing any of the symptoms above, don't leave it — a failing clutch will only get worse and could leave you stranded. Call us on **${NAP.phone}** for an honest assessment.`,
  },
  {
    slug: "brake-repair-near-me-east-london",
    title: "Brake Repair Near Me — East London Brake Specialists",
    description: "Need brake repair in East London? Pads, discs, calipers and fluid — we fit quality parts for all makes. Woodseer Autos, Poplar E14.",
    date: "2026-04-08",
    readTime: "4 min",
    category: "Repairs",
    tags: ["Brakes", "East London", "Brake Repair", "E14"],
    content: `Your brakes are the most important safety system on your vehicle. If they're not working properly, it's not something to put off.

## Common Brake Problems We Fix

- **Worn pads** — the most frequent brake repair. Pads wear down over time and need replacing before they damage the discs.
- **Worn or warped discs** — discs can wear thin or warp from heat, causing vibration or pulsing through the pedal.
- **Seized calipers** — calipers can seize, causing uneven wear and pulling to one side under braking.
- **Brake fluid** — old brake fluid absorbs moisture over time, reducing braking performance. We recommend changing it every 2 years.
- **Handbrake adjustment** — cables stretch over time. If your handbrake doesn't hold properly, it needs adjusting or replacing.
- **Brake hoses** — rubber hoses can deteriorate and crack, causing fluid leaks.

## What We Check

Every brake job at Woodseer Autos includes a full inspection of the entire braking system — not just the component that's causing the immediate problem. We check:

- Pad thickness on all wheels
- Disc condition and thickness
- Caliper operation and condition
- Brake fluid level and condition
- Hoses and pipes for leaks or damage
- Handbrake operation and adjustment

## How Long Does a Brake Repair Take?

- **Pads only**: Around 1 hour per axle
- **Pads and discs**: 1.5–2 hours per axle
- **Caliper replacement**: 2–3 hours depending on the vehicle
- **Full brake overhaul**: Half a day to a full day

## Our Approach

We use quality OE-equivalent parts as standard. We don't fit the cheapest option — because cheap brake parts compromise your safety. You'll get a clear quote before we start, and we'll explain what needs doing and why.

## Book a Brake Check

If you're hearing noises, feeling vibration, or your braking doesn't feel right — bring it in. Call **${NAP.phone}** or WhatsApp us.`,
  },
  {
    slug: "engine-diagnostics-isle-of-dogs",
    title: "Engine Diagnostics on the Isle of Dogs — Find the Fault",
    description: "Engine warning light on? Professional diagnostics near Isle of Dogs. We find the root cause, not just clear the code. Woodseer Autos, E14.",
    date: "2026-04-06",
    readTime: "4 min",
    category: "Diagnostics",
    tags: ["Diagnostics", "Isle of Dogs", "Warning Lights", "E14"],
    content: `If you live on the Isle of Dogs and a warning light has appeared on your dashboard, you need a garage that can actually diagnose the problem — not just plug in a reader and clear the code.

## Why Code Clearing Doesn't Work

Some garages will plug in a basic code reader, tell you what the code says, clear it and charge you. The light comes back a week later, and you're paying again.

At Woodseer Autos, we take a different approach. We use professional-grade diagnostic equipment to read the codes, then we **interpret them in context** with a physical inspection and live data analysis. We find the actual cause — not just the symptom.

## Common Warning Lights We Diagnose

- **Engine management light** — could be anything from a sensor fault to a misfire. The code alone rarely tells the full story.
- **ABS warning** — anti-lock braking system fault. Usually a wheel speed sensor or module issue.
- **Airbag light** — seat sensor, wiring fault, or module issue. Needs proper diagnosis.
- **DPF warning** — diesel particulate filter blocked or regeneration issue.
- **Traction control** — often linked to ABS faults or wheel speed sensor problems.
- **Battery/charging** — alternator, battery or wiring issue.

## What Happens When You Bring Your Car In

1. You tell us what you've noticed — warning lights, behaviour changes, noises
2. We connect professional diagnostic equipment and read all stored fault codes
3. We run live data tests to see how systems are performing in real time
4. We carry out a physical inspection of relevant components
5. We give you a clear, honest report with repair options and costs

## How Far Are We from Isle of Dogs?

We're at 16 Pixley Street, E14 7DF — about 5 minutes by car from Mudchute, Millwall and the wider Isle of Dogs area. Easy to reach via Manchester Road or the A1261.

Call **${NAP.phone}** or WhatsApp us a photo of the warning light and we'll advise.`,
  },
  {
    slug: "bodywork-repair-docklands-east-london",
    title: "Bodywork Repair in Docklands — Dents, Scratches & More",
    description: "Professional bodywork and paint repair in Docklands and East London. Dent removal, scratch repair, bumper repair and colour matching. Woodseer Autos.",
    date: "2026-04-05",
    readTime: "4 min",
    category: "Bodywork",
    tags: ["Bodywork", "Docklands", "East London", "Paint Repair"],
    content: `Docklands car parks, narrow residential streets, and tight underground garages — if you live or work in the Docklands area, your car is going to pick up the odd ding, scrape or scratch. Here's how we can help.

## Common Damage We Repair

- **Car park dents** — other people's doors are the number one cause of bodywork damage in urban areas
- **Bumper scuffs** — low-speed parking bumps, bollards, or tight spaces
- **Scratches** — from keys, shopping trolleys, or brushing against walls
- **Paint chips** — stone chips on the bonnet and front bumper from motorway driving
- **Collision damage** — from minor road incidents
- **Rust spots** — where paint has been damaged and moisture has got in

## Our Repair Process

1. **Assessment** — bring the car in or send us photos via WhatsApp for a rough estimate
2. **Quote** — we give you a clear, written quote before any work starts
3. **Preparation** — we mask, sand and prime the affected area to ensure proper adhesion
4. **Repair** — dent filling, panel reshaping, or paintless dent removal where possible
5. **Paint** — professional colour matching using computerised systems to blend seamlessly
6. **Finish** — clear coat, polish and final inspection

## Colour Matching

We use computerised colour matching to ensure the repair blends perfectly with your existing paintwork. Every vehicle's paint fades slightly over time, so matching the paint code alone isn't enough — we match the actual colour as it looks now.

## Can I Claim on Insurance?

We can provide detailed written assessments and quotes to support your insurance claim. Many of our customers choose to pay privately for minor damage to avoid affecting their no-claims bonus — but the choice is yours. We'll give you honest advice either way.

## Where We Are

We're at 16 Pixley Street, E14 7DF — easily accessible from the wider Docklands area including Canary Wharf, West India Quay, Limehouse and Crossharbour.

Call **${NAP.phone}** or WhatsApp us photos of the damage for a quick estimate.`,
  },
  {
    slug: "car-garage-tower-hamlets-all-services",
    title: "Local Car Garage in Tower Hamlets — All Services in One",
    description: "Independent car garage in Tower Hamlets offering MOT testing, servicing, diagnostics, repairs and bodywork. Woodseer Autos, E14 7DF.",
    date: "2026-04-04",
    readTime: "4 min",
    category: "General",
    tags: ["Tower Hamlets", "East London", "Garage", "E14"],
    content: `Finding a trustworthy local garage isn't always easy. In Tower Hamlets, you've got plenty of options — but most drivers are looking for the same thing: honesty, competence and fair pricing.

## What We Offer

At Woodseer Autos, we cover everything your vehicle needs:

- **MOT Testing** — authorised station, Classes 1–5 and 7. Most tests done within the hour.
- **Car Servicing** — interim, full and major services for all makes and models. Service book stamped.
- **Diagnostics** — professional fault-finding with OBD-II equipment. We find the cause, not just the code.
- **Mechanical Repairs** — brakes, clutch, gearbox, suspension, steering, engine work and more.
- **Bodywork** — dent removal, scratch repair, paint matching, bumper repair and collision damage.

## Why Local Drivers Choose Us

We're not the cheapest garage in Tower Hamlets, and we're not the most expensive. We're the one that does the job properly, explains what's going on, and charges you fairly for it.

- We won't recommend work you don't need
- We'll give you a clear quote before starting
- We'll keep you updated throughout
- We'll stamp your service book and use quality parts
- We'll be straight with you — always

## All Makes and Models

We work on everything from daily commuters and family cars to vans, executive saloons and performance vehicles. If it's got wheels and an engine, we can probably help.

## Where to Find Us

We're at 16 Pixley Street, London E14 7DF. Entrance via 14 Copenhagen Place — through the driveway past the parking spaces.

We serve drivers across Tower Hamlets including Poplar, Canary Wharf, Isle of Dogs, Limehouse, Bow, Bethnal Green and Mile End.

## Get in Touch

Call **${NAP.phone}** or WhatsApp us. We're open Monday to Saturday, 9am to 6pm.`,
  },
  {
    slug: "driving-with-expired-mot-rules-grace-period",
    title: "Driving With an Expired MOT: UK Rules & Grace Period (2026)",
    description: "Is there an MOT grace period in the UK? Can you drive to a pre-booked test? Fines, insurance impact and the rules explained by an E14 MOT station.",
    date: "2026-06-10",
    readTime: "7 min",
    category: "MOT",
    tags: ["MOT", "Expired MOT", "DVSA", "East London", "E14"],
    content: `If your MOT has run out — even by a day — you're committing an offence the moment you drive on a public road. There is **no grace period in the UK**. The £1,000 fine, possible 6–8 penalty points, and a void insurance policy are all on the table. There are only a few narrow exceptions. Here are the rules, written by a [DVSA-authorised MOT station in East London](/mot).

## Is There an MOT Grace Period in the UK?

No. The "grace period" most drivers have heard about is a myth. The rule is simple:

- Your MOT expires at **midnight on the date shown** on the certificate.
- From that moment, the car cannot legally be driven on a public road.
- The only legal exceptions are listed below — and "I forgot" is not one of them.

You can, however, book and pass the test **up to one month and one day before** the current certificate expires. The new certificate then runs for 13 months — so booking early loses you nothing. Check your expiry free at [gov.uk/check-mot-status](https://www.gov.uk/check-mot-status).

## Can I Drive to a Pre-Booked MOT Test?

Yes — this is the one exception drivers actually use. You **can** drive a car with an expired MOT to:

- A pre-booked MOT test at a DVSA-approved station, or
- A garage to have pre-MOT repairs done that are required to pass the test.

Two important caveats:

1. The booking must be genuine and pre-arranged. Police can ask the garage to confirm.
2. The car must still be **roadworthy and insured**. If it isn't, the exception doesn't apply and you're back to the £1,000 fine plus a possible "using a vehicle in a dangerous condition" charge.

If your car isn't roadworthy, transport it on a trailer or call us — we can advise on the safest route.

## What Are the Fines and Penalties?

| Offence | Penalty |
| --- | --- |
| Driving without a valid MOT | Up to £1,000 fine |
| Driving a vehicle in dangerous condition (no MOT + defect) | Up to £2,500 fine, 3 penalty points, possible driving ban |
| Insurance invalidated by no MOT | Claim refused; IN10 conviction possible if uninsured |

Most insurers treat an expired MOT as a **breach of policy conditions**. Your cover may technically still meet the legal minimum for third-party claims, but your own car damage, theft, and windscreen claims are likely refused — and the insurer can recover any third-party payout from you.

## What About SORN Vehicles?

If the car is declared SORN (off the road) it doesn't need an MOT — but it also can't be driven anywhere except a pre-booked test. Same one exception, same rules.

## How Quickly Can I Get an MOT in E14?

We usually have same-day or next-day slots at our Poplar workshop. Book online or call **${NAP.phone}** and we'll fit you in — most cars are tested in about 45 minutes. If anything fails, you'll get a clear written quote and we can sort the repairs the same week. See live availability on our [MOT booking page](/mot).

## What To Do If Your MOT Has Already Expired

1. **Don't drive** — except directly to a pre-booked test.
2. Book the test online or call us.
3. If the car isn't roadworthy, arrange recovery to the garage.
4. Pass the test and you're legal again immediately — the new certificate is valid for 12 months from the test date (not from the old expiry).

## Where to Find Us

16 Pixley Street, London E14 7DF — yard entrance via 14 Copenhagen Place. Open Monday to Saturday, 9am to 6pm. We serve drivers across Poplar, Canary Wharf, Isle of Dogs, Limehouse, Bow, Bethnal Green and Mile End.

Call **${NAP.phone}** or [book your MOT online](/mot).`,
  },
];


